import { Box, ClickAwayListener, Fade, Paper, Popper } from '@mui/material';
import {
    FacebookShareButton, FacebookIcon,
    TwitterShareButton,
    EmailShareButton, EmailIcon,
    LinkedinShareButton, LinkedinIcon,
    TelegramShareButton, TelegramIcon,
    WhatsappIcon,
    FacebookMessengerShareButton, FacebookMessengerIcon
} from 'react-share';

import { useMemo } from 'react';
import { useSendStoreLogs } from '../../../hooks/StoreLogs/useSendStoreLogs';
import { ReactComponent as X } from './../../../assets/icons/X.svg';
import { buildSharePayload, tryNativeShare } from '../../../utils/nativeShare';

const openWhatsAppShareFallback = (text) => {
    // Generic WhatsApp share URL — does not target a specific package.
    // Android may still open a default app if the user previously set one.
    window.open(
        `https://wa.me/?text=${encodeURIComponent(text)}`,
        '_blank',
        'noopener,noreferrer'
    );
};

const Share = ({ anchorEl, open, main_url, name, onClose }) => {
    const url = main_url;
    const message = `Check out this: ${name}`;
    const { storeLogs: sendLogs } = useSendStoreLogs();

    const modifiers = useMemo(() => [
        { name: 'offset', options: { offset: [0, 8] } },
        { name: 'preventOverflow', options: { padding: 8 } },
    ], []);

    const popperSx = useMemo(() => ({ zIndex: 9999 }), []);

    const handleWhatsAppShare = async (event) => {
        event.preventDefault();
        event.stopPropagation();
        sendLogs('shares');

        const shareText = url ? `${message} ${url}` : message;
        const shareData = buildSharePayload({ name, url });

        // From inside the popup, prefer native sheet when available
        const result = await tryNativeShare(shareData);
        if (result === 'shared' || result === 'aborted') {
            onClose?.();
            return;
        }

        openWhatsAppShareFallback(shareText);
        onClose?.();
    };

    return (
        <Popper
            open={open}
            anchorEl={anchorEl}
            placement="bottom-start"
            transition
            modifiers={modifiers}
            sx={popperSx}
        >
            {({ TransitionProps }) => (
                <Fade {...TransitionProps} timeout={200}>
                    <Paper elevation={3} sx={{ p: 1.5, borderRadius: 2, bgcolor: 'background.paper' }}>
                        <ClickAwayListener onClickAway={onClose}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: 1.5,
                                    '& button': {
                                        width: 40,
                                        height: 40,
                                        p: 0,
                                        minWidth: 'auto',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        transition: 'transform 0.2s',
                                        '&:hover': { transform: 'scale(1.1)' },
                                        bgcolor: 'transparent',
                                    },
                                    '& button svg': {
                                        width: 32,
                                        height: 32,
                                    },
                                }}
                            >
                                <FacebookShareButton
                                    url={url}
                                    beforeOnClick={() => sendLogs('shares')}
                                    onShareWindowClose={onClose}
                                >
                                    <FacebookIcon size={32} round />
                                </FacebookShareButton>
                                <FacebookMessengerShareButton
                                    url={url}
                                    appId={534790826284453}
                                    beforeOnClick={() => sendLogs('shares')}
                                    onShareWindowClose={onClose}
                                >
                                    <FacebookMessengerIcon size={32} round />
                                </FacebookMessengerShareButton>
                                <TwitterShareButton
                                    url={url}
                                    beforeOnClick={() => sendLogs('shares')}
                                    onShareWindowClose={onClose}
                                >
                                    <Box
                                        sx={{
                                            width: 30,
                                            height: 30,
                                            borderRadius: '50%',
                                            bgcolor: '#000',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            transition: 'transform 0.2s',
                                            '&:hover': { transform: 'scale(1.1)' },
                                        }}
                                    >
                                        <X style={{ width: 20, height: 20, color: '#fff' }} />
                                    </Box>
                                </TwitterShareButton>

                                <EmailShareButton
                                    url={url}
                                    subject={message}
                                    body={message}
                                    separator=" - "
                                    openShareDialogOnClick
                                    beforeOnClick={() => sendLogs('shares')}
                                    onShareWindowClose={onClose}
                                >
                                    <EmailIcon size={32} round />
                                </EmailShareButton>

                                <LinkedinShareButton
                                    url={url}
                                    beforeOnClick={() => sendLogs('shares')}
                                    onShareWindowClose={onClose}
                                >
                                    <LinkedinIcon size={32} round />
                                </LinkedinShareButton>

                                <TelegramShareButton
                                    url={url}
                                    beforeOnClick={() => sendLogs('shares')}
                                    onShareWindowClose={onClose}
                                >
                                    <TelegramIcon size={32} round />
                                </TelegramShareButton>

                                <button
                                    type="button"
                                    aria-label="Share on WhatsApp"
                                    onClick={handleWhatsAppShare}
                                >
                                    <WhatsappIcon size={32} round />
                                </button>
                            </Box>
                        </ClickAwayListener>
                    </Paper>

                </Fade>
            )}
        </Popper>
    );
};

export default Share;
